// Админ (04 §9) — the queue of things needing a decision, the health of the machine, and the
// config the owner is meant to change without asking anyone.
//
// The review queue comes FIRST, deliberately. Everything else on this screen is optional
// housekeeping; the queue is the pipeline saying "I could not decide this, and I refused to
// guess". Leaving it buried under settings tabs would let those refusals pile up silently, which
// is the one failure mode the whole no-guessing design cannot survive.

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HealthPanel } from './HealthPanel.js';
import { QueriesEditor } from './QueriesEditor.js';
import { SettingsEditor } from './SettingsEditor.js';
import { VerticalsSection } from './verticals/VerticalsSection.js';
import { CitiesSection } from './cities/CitiesSection.js';
import { NicheWizard } from './niche-wizard/NicheWizard.js';
import { useResolveReview, useReviewQueue } from './useAdmin.js';
import { useApplyManualEntity } from '../leads/useActivity.js';
import './admin.css';

const SECTIONS = [
  { key: 'review', label: 'За проверка' },
  { key: 'health', label: 'Състояние на системата' },
  { key: 'queries', label: 'Заявки за търсене' },
  { key: 'verticals', label: 'Ниши' },
  { key: 'cities', label: 'Градове и райони' },
  { key: 'wizard', label: 'Нова ниша' },
  { key: 'settings', label: 'Конфигурация' },
] as const;

type SectionKey = (typeof SECTIONS)[number]['key'];

function isSection(value: string | null): value is SectionKey {
  return SECTIONS.some((entry) => entry.key === value);
}

/** Plain Bulgarian for what the pipeline meant. An issue_type is a code, not an explanation. */
const ISSUE_LABELS: Record<string, string> = {
  no_registry_match: 'Не е намерена фирма в Търговския регистър',
  registry_website_found: 'В регистъра е вписан сайт, различен от намерения',
  suppression_suspect: 'Телефонът съвпада с отписан контакт в черния списък',
  domain_group_conflict: 'Конфликт при групиране по домейн',
  shared_phone: 'Телефонът се споделя от над 3 обекта в същия град',
  pii_quarantined: 'Личните данни са маскирани след 24 месеца без контакт',
};

function ReviewEikAction({ leadId }: { leadId: number }) {
  const [eik, setEik] = useState('');
  const apply = useApplyManualEntity();
  const valid = /^\d{9}(\d{4})?$/.test(eik.trim());

  return (
    <form
      style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        apply.mutate({ leadId, eik: eik.trim() });
      }}
    >
      <input
        type="text"
        className="filter-input"
        style={{ width: '130px', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
        placeholder="ЕИК (9/13 цифри)"
        value={eik}
        onChange={(e) => setEik(e.target.value)}
      />
      <button type="submit" className="adm__resolve-btn" disabled={!valid || apply.isPending}>
        {apply.isPending ? '…' : 'Приложи'}
      </button>
    </form>
  );
}

function ReviewQueue() {
  const { data: items = [], isLoading, error } = useReviewQueue();
  const resolve = useResolveReview();

  if (isLoading) return <p className="adm__msg">Зареждане на опашката за проверка…</p>;
  if (error) return <p className="adm__err">Грешка при зареждане: {error.message}</p>;
  if (items.length === 0) {
    return (
      <div className="adm__empty-card">
        <span className="adm__empty-icon">✓</span>
        <h3>Всичко е проверено</h3>
        <p className="adm__msg">Няма нерешени казуси. Пайплайнът работи без спиране.</p>
      </div>
    );
  }

  return (
    <div className="adm__queue-wrap">
      <div className="adm__queue-head">
        <h3 className="adm__h">Случаи за ръчно одобрение ({items.length})</h3>
        <p className="adm__note">
          Пайплайнът спира пред тези обекти, за да не прави грешни допускания вместо вас.
        </p>
      </div>

      <ul className="adm__queue">
        {items.map((item) => (
          <li key={item.id} className="adm__item">
            <div className="adm__itemmain">
              <div className="adm__item-top">
                <Link to={`/leads/${item.lead_id}`} className="adm__itemname">
                  {item.business_name}
                </Link>
                <span className={`adm__itemtype-badge adm__itemtype--${item.issue_type}`}>
                  {item.issue_type}
                </span>
              </div>
              <span className="adm__itemtype">
                {ISSUE_LABELS[item.issue_type] ?? item.issue_type}
              </span>
              {item.notes ? <span className="adm__itemnote">{item.notes}</span> : null}
            </div>
            <div
              className="adm__itemside"
              style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
            >
              <span className="adm__when">
                {new Date(item.created_at).toLocaleDateString('bg-BG')}
              </span>
              {item.issue_type === 'no_registry_match' ? (
                <ReviewEikAction leadId={item.lead_id} />
              ) : null}
              <button
                type="button"
                className="adm__resolve-btn"
                onClick={() => resolve.mutate(item.id)}
              >
                ✓ Одобри
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AdminPage() {
  // URL-addressable (plan §4.3): the wizard links to `?section=queries&batch=…`.
  const [params, setParams] = useSearchParams();
  const requested = params.get('section');
  const section: SectionKey = isSection(requested) ? requested : 'review';
  const setSection = (key: SectionKey) => setParams(key === 'review' ? {} : { section: key });
  const { data: items = [] } = useReviewQueue();

  return (
    <div className="adm">
      <nav className="adm__nav">
        {SECTIONS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className={`adm__tab ${section === entry.key ? 'adm__tab--on' : ''}`}
            onClick={() => setSection(entry.key)}
          >
            {entry.label}
            {entry.key === 'review' && items.length > 0 ? (
              <span className="adm__badge">{items.length}</span>
            ) : null}
          </button>
        ))}
      </nav>

      <div className="adm__body">
        {section === 'review' ? <ReviewQueue /> : null}
        {section === 'health' ? <HealthPanel /> : null}
        {section === 'queries' ? <QueriesEditor /> : null}
        {section === 'verticals' ? <VerticalsSection /> : null}
        {section === 'cities' ? <CitiesSection /> : null}
        {section === 'wizard' ? <NicheWizard /> : null}
        {section === 'settings' ? <SettingsEditor /> : null}
      </div>
    </div>
  );
}
